import { config } from '../config';
import { setCorrelationId, log } from '../app/logger';
import { errorMessage } from '../app/errors';
import { captureEnvironment } from './environment';
import { probeStorage } from './storageProbe';
import { currentSeedProfile, pickTargetHypothesisId } from './seed';
import { prepareConnection, runAuto, withWakeLock } from './runner';
import type { Pacing } from './runner';
import { foreignHeatWorkers, soak, ThermalLoad } from './thermalLoad';
import { acquireRunLock } from './runLock';
import { saveRun } from './resultsStore';
import type { RunCondition, RunRecord, TrialSample } from '../types';
import { MeasurementError } from '../app/errors';

/**
 * Composes one complete measurement run: prepare → soak (thermal only) → measure → probe →
 * assemble → persist.
 *
 * Kept separate from `runner.ts` so the measured window itself stays small and readable. Anything
 * this file does happens strictly outside the measured window.
 */

const MODULE = 'session';

export interface SessionOptions {
  condition: RunCondition;
  trials: number;
  pacing: Pacing;
  /** Seconds of sustained load before the thermal condition measures. 0 = "the phone is already hot". */
  soakSeconds: number;
  /** BAK-3 — whether to call `navigator.storage.persist()` on this run. */
  requestPersist: boolean;
  onPhase: (phase: string, detail?: string) => void;
  onProgress: (done: number, total: number, lastSample: TrialSample | null) => void;
  shouldStop: () => boolean;
}

function newRunId(): string {
  return `u0-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 7)}`;
}

/**
 * @throws {MeasurementError} when the database holds no hypothesis to increment (seed first), or
 *   when every trial failed. Both are surfaced on screen; neither produces an empty "passing" run.
 */
export async function runSession(options: SessionOptions): Promise<RunRecord> {
  const runId = newRunId();
  const startedAt = Date.now();
  setCorrelationId(runId);
  log.info(MODULE, 'run starting', { condition: options.condition, trials: options.trials });

  const now = () => performance.now();
  const pageLoadMs = 0; // `performance.now()` is already measured from page load.

  const hypothesisId = await pickTargetHypothesisId();
  if (!hypothesisId) {
    throw new MeasurementError(
      'The harness database has no hypotheses. Seed it before measuring — a commit into an empty store is not the transaction the product performs.',
    );
  }
  const matchId = hypothesisId.replace(/^h-(\d+)-\d+$/, 'm-$1');

  // Claimed BEFORE the load generator starts and held until after it stops. The contamination this
  // prevents came from a second run's *soak* overlapping the first run's sampling, so a lock that
  // spanned only the sampling window would not have caught it.
  const releaseLock = acquireRunLock(runId, `${options.condition} latency`, Date.now());

  const thermal = new ThermalLoad(runId);
  let soakCompletedS = 0;
  let foreignHeatWorkersDuringRun = foreignHeatWorkers(runId);

  const record = await withWakeLock(async () => {
    try {
      if (options.condition === 'thermal') {
        options.onPhase('Heating', `${thermal.workerCount} workers`);
        thermal.start(now);
        if (options.soakSeconds > 0) {
          soakCompletedS = await soak(
            options.soakSeconds,
            (remaining) => options.onPhase('Heating', `${remaining}s remaining`),
            options.shouldStop,
          );
        }
      }

      options.onPhase('Preparing connection');
      await prepareConnection(options.condition, now);

      options.onPhase('Measuring');
      const result = await runAuto({
        trials: options.trials,
        warmups: options.condition === 'cold' ? 0 : config.HARNESS_WARMUP_TRIALS,
        pacing: options.pacing,
        hypothesisId,
        matchId,
        now,
        pageLoadMs,
        // Sampled per trial rather than once at each end, so a foreign load that starts and stops
        // entirely inside the window is still recorded. The peak is what is kept.
        onProgress: (done, total, lastSample) => {
          foreignHeatWorkersDuringRun = Math.max(
            foreignHeatWorkersDuringRun,
            foreignHeatWorkers(runId),
          );
          options.onProgress(done, total, lastSample);
        },
        shouldStop: options.shouldStop,
      });
      foreignHeatWorkersDuringRun = Math.max(foreignHeatWorkersDuringRun, foreignHeatWorkers(runId));

      if (result.samples.length === 0) {
        throw new MeasurementError(
          `Every trial failed — no timing was produced. First failure: ${result.failures[0]?.message ?? 'unknown'}`,
        );
      }

      options.onPhase('Probing storage');
      const storage = await probeStorage(options.requestPersist);
      const seed = await currentSeedProfile();

      const run: RunRecord = {
        id: runId,
        startedAt,
        finishedAt: Date.now(),
        condition: options.condition,
        mode: 'auto',
        soakCompletedS,
        heatWorkers: thermal.workerCount,
        foreignHeatWorkersDuringRun,
        warmupTrials: result.warmupTrials,
        samples: result.samples,
        seed,
        storage,
        environment: captureEnvironment(),
        logFlushesDuringRun: result.logFlushesDuringRun,
        failures: result.failures,
        notes: null,
      };

      return run;
    } finally {
      // Order matters: the workers must be gone before the lock is released, or the next run could
      // start heating while this one's load is still burning — the exact overlap being prevented.
      if (options.condition === 'thermal') thermal.stop(now);
      releaseLock();
    }
  });

  if ((record.foreignHeatWorkersDuringRun ?? 0) > 0) {
    log.error(
      MODULE,
      'another run’s heat workers were live during this run’s sampling window; its figures describe a load it did not ask for',
      { runId, foreignHeatWorkersDuringRun: record.foreignHeatWorkersDuringRun },
    );
  }

  options.onPhase('Saving');
  try {
    await saveRun(record);
  } catch (cause) {
    // The measurement is in hand; only its persistence failed. Return it so the screen can still
    // display and export it, and make the failure visible rather than pretending it saved.
    log.error(MODULE, 'run could not be persisted; it exists only in memory', {
      error: errorMessage(cause),
    });
    return { ...record, notes: `NOT SAVED TO DISK: ${errorMessage(cause)} — export before leaving this screen.` };
  } finally {
    setCorrelationId(null);
  }

  log.info(MODULE, 'run complete', { id: runId, n: record.samples.length });
  return record;
}

/**
 * A claim on the run lock, held for as long as the live-tap pad is on screen.
 *
 * The pad is open-ended — the operator taps for as long as they like and finishes when they choose
 * — so its lifetime is not something the session layer can bound. Handing the caller an explicit
 * lease makes the pairing visible: `finalizeLiveTapRun` cannot be called without one, and
 * `release()` is what the pad's unmount path calls so an abandoned session cannot wedge the lock.
 */
export interface LiveTapLease {
  runId: string;
  /** Called on every tap, so a foreign load that appears mid-session is caught, not just its ends. */
  observeForeignLoad: () => void;
  foreignPeak: () => number;
  release: () => void;
}

/**
 * Claims the run lock for a live-tap session.
 *
 * @throws {MeasurementError} if another run is in progress.
 */
export function beginLiveTapRun(): LiveTapLease {
  const runId = newRunId();
  const releaseLock = acquireRunLock(runId, 'live-tap', Date.now());

  let peak = foreignHeatWorkers(runId);
  return {
    runId,
    observeForeignLoad: () => {
      peak = Math.max(peak, foreignHeatWorkers(runId));
    },
    foreignPeak: () => peak,
    release: releaseLock,
  };
}

/**
 * Assembles a run record from samples produced by real taps on the live-tap pad.
 *
 * Auto mode anchors on the moment before dispatch, which excludes everything between the finger
 * touching the glass and JavaScript running. NFR-1's budget does not exclude that. Live-tap mode
 * anchors on the real `pointerup`, so `inputMs` is populated and `totalMs` is the honest
 * tap-to-acknowledgment figure. Auto mode gives the statistical power; this gives the anchor.
 */
export async function finalizeLiveTapRun(
  lease: LiveTapLease,
  condition: RunCondition,
  samples: TrialSample[],
  failures: { index: number; message: string }[],
  requestPersist: boolean,
): Promise<RunRecord> {
  if (samples.length === 0) {
    throw new MeasurementError('No taps were recorded, so there is nothing to score.');
  }

  lease.observeForeignLoad();
  const storage = await probeStorage(requestPersist);
  const seed = await currentSeedProfile();

  const record: RunRecord = {
    id: lease.runId,
    startedAt: samples[0] ? Date.now() - samples[0].sinceLoadMs : Date.now(),
    finishedAt: Date.now(),
    condition,
    mode: 'liveTap',
    soakCompletedS: 0,
    heatWorkers: 0,
    foreignHeatWorkersDuringRun: lease.foreignPeak(),
    warmupTrials: 0,
    samples,
    seed,
    storage,
    environment: captureEnvironment(),
    logFlushesDuringRun: 0,
    failures,
    notes:
      samples.length < config.HARNESS_TRIALS_MIN
        ? `Live taps — anchored on the real pointerup, so inputMs is populated. Finished at ${samples.length} taps, below the ${config.HARNESS_TRIALS_MIN}-tap admissibility floor: the samples are kept but this run is not evidence.`
        : 'Live taps — anchored on the real pointerup, so inputMs is populated.',
  };

  try {
    await saveRun(record);
  } catch (cause) {
    log.error(MODULE, 'live-tap run could not be persisted', { error: errorMessage(cause) });
    return { ...record, notes: `NOT SAVED TO DISK: ${errorMessage(cause)} — export before leaving.` };
  }
  return record;
}
